import React, { useEffect, useState } from 'react';
import UiCopyBlock from '../components/UiCopyBlock';

/** Returns override when non-empty after trim, else fallback. */
function getUiText(override: string | undefined, fallback: string): string {
  const t = (override ?? '').trim();
  return t.length > 0 ? t : fallback;
}

interface SuccessPageProps {
  hospitalId?: string;
  hospitalName?: string;
  logoUrl?: string | null;
  successTitle?: string;
  successDescription?: string;
  successTopText?: string;
  successBottomText?: string;
}

const SuccessPage: React.FC<SuccessPageProps> = ({ 
  hospitalName,
  logoUrl,
  successTitle,
  successDescription,
  successTopText,
  successBottomText,
}) => {
  const [mounted, setMounted] = useState(false);

  // Trigger entrance animation on mount
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="min-h-[100dvh] bg-slate-50 font-sans flex flex-col items-center justify-center p-4 sm:p-6 selection:bg-emerald-100 relative overflow-hidden">
      
      {/* Background Ambient Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-emerald-50/40 rounded-full blur-[100px] pointer-events-none -z-10" />

      {/* Main Content Wrapper (Animated) */}
      <div 
        className={`w-full max-w-xl relative z-10 flex flex-col items-center transform transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}
      >
        
        {/* Hospital Branding */}
        <div className="flex flex-col items-center mb-8 h-56 sm:h-72 justify-end">
          {logoUrl ? (
            <img 
              src={logoUrl}
              alt={`${hospitalName || 'Spital'} logo`}
              className="max-h-full w-auto object-contain drop-shadow-sm"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'block';
              }}
            />
          ) : null}
          {/* Fallback & Name */}
          {!logoUrl && hospitalName && (
            <p className="text-sm font-bold tracking-[0.2em] text-slate-400 uppercase text-center">
              {hospitalName}
            </p>
          )}
          {logoUrl && (
            <p className="hidden text-sm font-bold tracking-[0.2em] text-slate-400 uppercase text-center">
              {hospitalName}
            </p>
          )}
        </div>

        {/* Main Success Card */}
        <div className="w-full bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-xl shadow-slate-200/50 border border-slate-100/80 px-6 py-10 sm:px-10 sm:py-12 flex flex-col items-center text-center relative overflow-hidden">
          
          {/* Subtle top glow effect inside card */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-24 bg-emerald-100/50 blur-3xl opacity-50 pointer-events-none" />

          {/* Success top copy */}
          {successTopText && (
            <div className="mb-6 w-full relative z-10">
              <UiCopyBlock text={successTopText} />
            </div>
          )}

          {/* Success Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 mb-6 relative z-10">
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm shadow-emerald-500/20">
              <svg 
                className="w-3.5 h-3.5 text-white" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                strokeWidth={3.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-sm font-bold tracking-wide uppercase text-emerald-700">
              Trimis cu succes
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight whitespace-pre-wrap relative z-10">
            {getUiText(successTitle, 'Mulțumim!')}
          </h1>
          
          {/* Subheading — no default copy; an empty "Descriere" renders nothing.
              The mb-10 lives on this element, so omitting it also drops the
              spacing rather than leaving a blank band above the info box. */}
          {successDescription?.trim() && (
            <p className="text-slate-500 text-base sm:text-lg leading-relaxed max-w-[90%] mx-auto whitespace-pre-wrap relative z-10 mb-10">
              {successDescription.trim()}
            </p>
          )}

          {/* Success bottom copy */}
          {successBottomText && (
            <div className="mt-8 w-full relative z-10 border-t border-slate-100 pt-6 text-left">
              <UiCopyBlock text={successBottomText} />
            </div>
          )}

        </div>

        {/* Bottom Completion Note */}
        <div 
          className={`flex items-center justify-center gap-2.5 mt-8 text-slate-400 transition-opacity duration-1000 delay-300 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="w-6 h-6 rounded-full bg-slate-200/50 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm font-medium">Ați finalizat. Puteți închide această pagină.</p>
        </div>

        {/* Footer */}
        <div 
          className={`mt-6 transition-opacity duration-1000 delay-500 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="text-center text-slate-400 text-xs font-medium tracking-wide">
            &copy; 2026 Powered by AXA Medis
          </p>
        </div>

      </div>
    </div>
  );
};

export default SuccessPage;